import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Collapse, Button } from 'react-bootstrap';
import cloneDeep from 'lodash.clonedeep';
import { useRequestData, useLabels } from 'wsm-common-data';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from 'ddc-track-event';
import {
	setPackagesSelections,
	setPrice,
	setVehicleConfiguration
} from '../actions/setVehicleConfiguration';
import { formatter } from '../helpers';
import Select from './Select';
import NoDataAvailable from './NoDataAvailable';
import stepController from '../controllers/StepController';

const Packages = ({ setCurrentStep }) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const navigate = useNavigate();
	setCurrentStep('packages');

	const { selectedTrimData, vehicleConfiguration, rootDir } = useSelector(
		(state) => state
	);
	const { widgetName, windowId } = useRequestData();

	const selectTrimDataDoesNotExists =
		Object.keys(selectedTrimData).length === 0;

	const [selectedPackages, setSelectedPackages] = useState(
		vehicleConfiguration.options.packages.selections
	);
	const [detailsOpenIndexes, setDetailsOpenIndexes] = useState([]);

	const handleSelect = ({ id, description, msrp }) => {
		const dupeI = selectedPackages.findIndex((pkg) => pkg.id === id);
		if (dupeI !== -1) {
			selectedPackages.splice(dupeI, 1);
			if (msrp) {
				dispatch(setPrice(vehicleConfiguration.price - msrp));
			}
		} else {
			selectedPackages.push({ id, description, msrp });
			if (msrp) {
				dispatch(
					setPrice(cloneDeep(vehicleConfiguration.price + msrp))
				);
			}
		}
		setSelectedPackages(selectedPackages);
		dispatch(setPackagesSelections(selectedPackages));
		const selectedPackageNames = selectedPackages.map(
			(item) => item.description
		);
		trackEvent(widgetName, windowId, {
			element: 'Package option',
			elementCTA: selectedPackageNames.join(', '),
			action: 'selected',
			result: 'Package check-box button selected'
		});
	};

	const handleToggleDetails = (index) => {
		const updatedIndexes = [...detailsOpenIndexes];
		const arrayIndex = detailsOpenIndexes.indexOf(index);
		if (arrayIndex > -1) {
			updatedIndexes.splice(arrayIndex, 1);
		} else {
			updatedIndexes.push(index);
		}
		setDetailsOpenIndexes(updatedIndexes);

		trackEvent(widgetName, windowId, {
			element: 'Package option',
			elementCTA: selectedTrimData.packages[index].description,
			action: 'clicked',
			result: 'Package details expanded'
		});
	};

	useEffect(() => {
		if (Object.keys(selectedTrimData).length === 0) {
			const lsVehicleConfig = stepController.getLsVehicleConfig();
			if (
				!lsVehicleConfig ||
				!lsVehicleConfig.make ||
				!lsVehicleConfig.model
			) {
				navigate(stepController.getRootPath());
			} else {
				dispatch(setVehicleConfiguration(lsVehicleConfig));
				const trimScreenPath = `/${rootDir}/${lsVehicleConfig.make}/${lsVehicleConfig.model}.htm`;
				navigate(trimScreenPath);
			}
		}
	}, [selectedTrimData]);

	return (
		<React.Fragment>
			{!selectTrimDataDoesNotExists &&
			selectedTrimData.packages.length ? (
				<form>
					{selectedTrimData.packages.map((data, index) => {
						const packagePrice = `+ ${formatter.format(
							Math.abs(data.msrp)
						)}`;
						const updatedData = {
							value: data.description,
							id: data.id
						};
						return (
							<React.Fragment key={`packages.group.${data.id}`}>
								<Select
									data={updatedData}
									detail={
										data.msrp
											? packagePrice
											: labels.get('NO_PRICING_AVAILABLE')
									}
									inputType="checkbox"
									itemIndex={index}
									handleSelect={() => handleSelect(data)}
									selected={
										selectedPackages.findIndex(
											(pkg) => pkg.id === data.id
										) !== -1
									}
									sideContent={
										<Button
											className="btn-link font-weight-normal"
											onClick={() =>
												handleToggleDetails(index)
											}
											aria-controls={`collapseDetails${index}`}
											aria-expanded={detailsOpenIndexes.includes(
												index
											)}
										>
											<small>
												{labels.get('DETAILS')}
											</small>
										</Button>
									}
									key={`${data.id}.select.package`}
								/>
								<Collapse
									in={detailsOpenIndexes.includes(index)}
								>
									<div className="text-left box box-border p-4">
										<React.Fragment>
											<ul className="list-group mb-0">
												{data.options.map(
													({ description, id }) => (
														<li
															key={id}
															className="list-group-item"
														>
															{description}
														</li>
													)
												)}
											</ul>
										</React.Fragment>
									</div>
								</Collapse>
							</React.Fragment>
						);
					})}
				</form>
			) : (
				<NoDataAvailable label={labels.get('NO_PACKAGES')} />
			)}
		</React.Fragment>
	);
};

Packages.propTypes = {
	setCurrentStep: PropTypes.func
};

export default Packages;
